import { ContentState } from "draft-js";
import { List } from "immutable";
import { isArray } from "lodash";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { Page } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { Post } from "@domain/post/post";
import { Carousel, CarouselStatus } from "./carousel";
import { CarouselApi } from "./carouselsApi";

export class CarouselService {
	carouselStore = new SimpleStore<Carousel>((id) => this.carouselApi.get(id));
	slugCarouselStore = new SimpleStore<Carousel>((slug) => this.carouselApi.getBySlug(slug));
	favoriteCarouselsPresentation = new StorePresentation(this.carouselStore, (page) =>
		this.carouselApi.getFavorites(page)
	);
	profileCarouselsStore = new SimpleMappedStorePresentation(this.carouselStore, (profileId, page) =>
		this.carouselApi.getProfileCarousels(profileId, page)
	);

	myCarouselsStore = new SimpleMappedStorePresentation(this.carouselStore, (status: CarouselStatus | "ALL", page) => {
		return this.carouselApi.getMyCarousels(status, page);
	});

	organizationCarouselsStore = new SimpleMappedStorePresentation(this.carouselStore, (organizationId, page) =>
		this.carouselApi.getOrganizationCarousels(organizationId, page)
	);

	constructor(
		private carouselApi: CarouselApi,
		private currentProfileService: CurrentProfileService,
		initialCarousels?: Map<string, Carousel>
	) {
		if (initialCarousels) {
			this.carouselStore.merge(List(initialCarousels.values()));
		}
		this.slugCarouselStore.bind(this.carouselStore);
	}

	async deleteFeedback(carouselId: string): Promise<void> {
		await this.carouselApi.deleteFeedback(carouselId);
		await this.carouselStore.fetch(carouselId);
	}

	async toggleFavorite(carousel: Carousel): Promise<void> {
		if (!carousel.isFavorite) {
			await this.carouselApi.setFavorite(carousel.id);
		} else {
			await this.carouselApi.removeFavorite(carousel.id);
		}
		this.carouselStore.update(carousel.id, (c) => ({ ...c, isFavorite: !carousel.isFavorite }));
	}

	async toggleInstructive(carousel: Carousel): Promise<void> {
		const initialInstructive = carousel.instructives.byMe;

		const carouselIntructif = (carousel: Carousel): Carousel => {
			return {
				...carousel,
				instructives: { byMe: !initialInstructive, total: carousel.instructives.total + (initialInstructive ? -1 : 1) },
			};
		};

		this.slugCarouselStore.update(carousel.slug, (c) => carouselIntructif(c));
		this.carouselStore.update(carousel.id, (c) => carouselIntructif(c));

		await (initialInstructive
			? this.carouselApi.removeInstructive(carousel.id)
			: this.carouselApi.setInstructive(carousel.id));
	}

	async shareCarousel(carouselId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.carouselApi.share(carouselId, message, groupId);
	}

	async saveCarousel(draft: Carousel, id?: string, authorId?: string): Promise<Carousel> {
		const carousel = await (id
			? this.carouselApi.saveCarousel(draft, id, authorId)
			: this.carouselApi.createCarousel(draft, authorId));
		return carousel;
	}

	async deleteCarousel(carouselId: string): Promise<void> {
		await this.carouselApi.deleteCarousel(carouselId);
		this.carouselStore.remove(carouselId);
	}

	suggest = (
		count: number,
		cat: Category | Category[] | undefined = undefined,
		language: ContentLanguage
	): Promise<Page<Carousel>> => {
		const categories: Category[] = cat
			? isArray(cat)
				? cat
				: [cat]
			: (this.currentProfileService.currentProfile.get()?.categories ?? []);
		return this.carouselApi.getSuggestions({ page: 0, size: count }, categories, language);
	};

	suggestByCarousel = (count: number, carouselId: string): Promise<Page<Carousel>> => {
		return this.carouselApi.getSuggestionsById({ page: 0, size: count }, carouselId);
	};

	search = this.carouselApi.search.bind(this.carouselApi);

	view(carouselId: string): Promise<void> {
		return this.carouselApi.view(carouselId);
	}

	incrementView(carousel: Carousel): Promise<void> {
		return this.carouselApi.view(carousel.id);
	}

	suggestCarouselBySameAuthor = (count: number, carouselId: string): Promise<Page<Carousel>> => {
		return this.carouselApi.getSuggestionsBySameAuthor({ page: 0, size: count }, carouselId);
	};

	suggestCarouselBySameCategory = (count: number, carouselId: string): Promise<Page<Carousel>> => {
		return this.carouselApi.getSuggestionsBySameCategory({ page: 0, size: count }, carouselId);
	};
}
